import { useEffect, useRef } from "react";

export default function StarField() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0, h = 0, stars = [], meteors = [], raf, mt;
    const mouse = { x: -9999, y: -9999 };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseout", onLeave);

    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * DPR;
      canvas.height = h * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      const count = Math.min(260, Math.floor((w * h) / 6500));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.3 + 0.3,
        base: Math.random() * 0.45 + 0.25,
        amp: Math.random() * 0.5 + 0.25,
        speed: Math.random() * 1.6 + 0.4,
        phase: Math.random() * Math.PI * 2,
        vx: (Math.random() - 0.5) * 0.08,
        vy: (Math.random() - 0.5) * 0.08,
        cphase: Math.random() * Math.PI * 2,
      }));
    };

    const spawnMeteor = () => {
      meteors.push({
        x: Math.random() * w * 0.7 + w * 0.15,
        y: -20,
        vx: -(Math.random() * 3 + 4),
        vy: Math.random() * 2 + 3,
        life: 1,
      });
      mt = setTimeout(spawnMeteor, Math.random() * 8000 + 6000);
    };

    let t = 0;
    const draw = () => {
      t += 0.016;
      ctx.clearRect(0, 0, w, h);

      const g = ctx.createRadialGradient(w * 0.72, h * 0.26, 0, w * 0.72, h * 0.26, Math.max(w, h) * 0.55);
      g.addColorStop(0, "rgba(255,255,255,0.05)");
      g.addColorStop(0.45, "rgba(255,255,255,0.018)");
      g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);

      for (const s of stars) {
        s.x = (s.x + s.vx + w) % w;
        s.y = (s.y + s.vy + h) % h;
        const a = s.base + Math.sin(t * s.speed + s.phase) * s.amp;
        ctx.globalAlpha = Math.max(0.06, Math.min(1, a));
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      const LINK = 110;
      for (let i = 0; i < stars.length; i++) {
        const a = stars[i];
        for (let j = i + 1; j < stars.length; j++) {
          const b = stars[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > LINK * LINK) continue;
          const d = Math.sqrt(d2);
          const form = 0.5 + 0.5 * Math.sin(t * 0.25 + (a.cphase + b.cphase) * 0.5);
          const alpha = (1 - d / LINK) * 0.16 * form;
          if (alpha < 0.008) continue;
          ctx.globalAlpha = alpha;
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
      ctx.globalAlpha = 1;

      const MLINK = 170;
      if (mouse.x > -999) {
        for (const s of stars) {
          const dx = s.x - mouse.x;
          const dy = s.y - mouse.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > MLINK * MLINK) continue;
          const d = Math.sqrt(d2);
          const tw = 0.65 + 0.35 * Math.sin(t * 2 + s.phase);
          ctx.globalAlpha = (1 - d / MLINK) * 0.4 * tw;
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(s.x, s.y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.stroke();
        }
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 1.6, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      meteors = meteors.filter((m) => m.life > 0 && m.y < h + 40);
      for (const m of meteors) {
        m.x += m.vx;
        m.y += m.vy;
        m.life -= 0.012;
        const tail = ctx.createLinearGradient(m.x, m.y, m.x - m.vx * 12, m.y - m.vy * 12);
        tail.addColorStop(0, `rgba(255,255,255,${0.7 * m.life})`);
        tail.addColorStop(1, "rgba(255,255,255,0)");
        ctx.strokeStyle = tail;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(m.x, m.y);
        ctx.lineTo(m.x - m.vx * 12, m.y - m.vy * 12);
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    mt = setTimeout(spawnMeteor, 3500);
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(mt);
      ro.disconnect();
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
    };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 w-full h-full" data-testid="hero-starfield" />;
}
